/**
 * Ad Banner Component
 * Displays ads using Google AdSense - ONLY shown during active gameplay
 * to comply with AdSense policies requiring meaningful publisher content.
 * 
 * The AdSense script is loaded dynamically only when this component renders,
 * ensuring no ads appear on content-light pages.
 */

import React, { useEffect, useRef, useState } from 'react';
import './AdBanner.css';

interface AdBannerProps {
  /** Ad unit ID from your ad network (e.g., Google AdSense) */
  adUnitId?: string;
  /** Ad slot ID (for Google AdSense) */
  adSlot?: string;
  /** Ad format: 'banner', 'rectangle', 'square' */
  format?: 'banner' | 'rectangle' | 'square';
  /** Custom ad code (for non-AdSense networks) */
  customAdCode?: string;
}

export const AdBanner: React.FC<AdBannerProps> = ({
  adUnitId,
  adSlot,
  format = 'banner',
  customAdCode,
}) => {
  const adRef = useRef<HTMLDivElement>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    // If custom ad code is provided, use it directly
    if (customAdCode && adRef.current) {
      adRef.current.innerHTML = customAdCode;
      return;
    }

    // Don't proceed without ad configuration
    if (!adUnitId || !adSlot) return;

    // Load AdSense script dynamically only when this component mounts
    // This ensures ads only load during active gameplay
    const loadAdSenseScript = () => {
      if (window.adsbygoogle !== undefined) {
        setScriptLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=' + adUnitId;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => setScriptLoaded(true);
      document.head.appendChild(script);
    };

    loadAdSenseScript();
  }, [adUnitId, adSlot, customAdCode]);

  // Initialize ad once script is loaded
  useEffect(() => {
    if (scriptLoaded && adUnitId && adSlot && adRef.current) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (err) {
        console.error('AdSense error:', err);
      }
    }
  }, [scriptLoaded, adUnitId, adSlot]);

  // Don't render if no ad configuration
  if (!adUnitId && !customAdCode) {
    return null;
  }

  const adClass = `ad-banner ad-${format}`;

  return (
    <div className={adClass} ref={adRef}>
      {adUnitId && adSlot && (
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-client={adUnitId}
          data-ad-slot={adSlot}
          data-ad-format={format === 'banner' ? 'auto' : 'rectangle'}
          data-full-width-responsive="true"
        />
      )}
    </div>
  );
};

// Extend Window interface for AdSense
declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

